import { AssignmentTimeDisplay } from '@/components/DataItem/AssignmentTimeDisplay'
import { AssignmentType } from '@/components/DataItem/AssignmentType'
import type { DotDropdownMenuActionItem } from '@/components/ui/DotMenu'
import { DotMenu } from '@/components/ui/DotMenu'
import {
  AlarmClockCheckIcon,
  Clock1Icon,
  ClockFadingIcon,
  DeleteIcon,
  EditIcon,
  EyeIcon,
  LibraryIcon,
  MoveRightIcon,
  PenIcon,
  RefreshCwIcon,
  type LucideProps
} from '@ttab/elephant-ui/icons'
import { type MouseEvent, useMemo, useState, useCallback, useEffect, useRef, type JSX } from 'react'
import { useLink } from '@/hooks/useLink'
import { Prompt } from '@/components'
import { Button, Tooltip } from '@ttab/elephant-ui'
import type { Block } from '@ttab/elephant-api/newsdoc'
import { deleteByYPath, getValueByYPath } from '@/shared/yUtils'
import { useOpenDocuments } from '@/hooks/useOpenDocuments'
import { cn } from '@ttab/elephant-ui/utils'
import { useNavigationKeys } from '@/hooks/useNavigationKeys'
import { CreateDeliverablePrompt } from './CreateDeliverablePrompt'
import { appendDocumentToAssignment } from '@/shared/createYItem'
import { createPayload } from '@/shared/templates/lib/createPayload'
import { Move } from '@/components/Move/'
import { useModal } from '@/components/Modal/useModal'
import type * as Y from 'yjs'
import { useRegistry } from '@/hooks/useRegistry'
import { useSession } from 'next-auth/react'
import { getDeliverableType } from '@/shared/templates/lib/getDeliverableType'
import { isVisualAssignmentType } from '@/defaults/assignmentTypes'
import { HastIndicator } from '@/components/HastIndicator'
import { CreatePrintArticle } from '@/components/CreatePrintArticle'
import { snapshotDocument } from '@/lib/snapshotDocument'
import { getTimeSlotTypes } from '@/defaults/assignmentTimeConstants'
import useSWR from 'swr'
import { useRepositoryEvents } from '@/hooks/useRepositoryEvents'
import { type YDocument, useYValue } from '@/modules/yjs/hooks'
import { toast } from 'sonner'
import { AssignmentStatus, getAssignmentStatusBorderClass, getAssignmentStatusBadgeBorderClass } from './AssignmentStatus'
import { getDocumentStatuses } from '@/defaults/documentStatuses'
import { useTranslation } from 'react-i18next'
import type { TranslationKey } from '@/types/i18next.d'
import { RelatedWires } from './RelatedWires'
import { useFeatureFlags } from '@/hooks/useFeatureFlags'
import { useConvertArticleType } from '@/hooks/useConvertArticleType'
import { ConvertToArticleDialog } from '@/components/ConvertToArticleDialog'
import { ConvertToTimelessDialog } from '@/components/ConvertToTimelessDialog'

export const AssignmentRow = ({ ydoc, index, onSelect, isFocused = false, asDialog }: {
  ydoc: YDocument<Y.Map<unknown>>
  index: number
  onSelect?: () => void
  isFocused?: boolean
  asDialog?: boolean
}): JSX.Element => {
  const openArticle = useLink('Editor')
  const openFlash = useLink('Flash')
  const openPlanning = useLink('Planning')

  const openDocuments = useOpenDocuments({ idOnly: true, name: 'Editor' })
  const { repository } = useRegistry()
  const { data: session } = useSession()
  const { t } = useTranslation()
  const featureFlags = useFeatureFlags(['hasPrint', 'hasHast', 'hasLooseSlugline'])
  const { isConverting } = useConvertArticleType()

  const base = `meta.core/assignment[${index}]`
  const [assignment] = useYValue<Y.Map<unknown>>(ydoc.ele, base, true)
  const [inProgress] = useYValue(assignment, '__inProgress')
  const [articleId] = useYValue<string>(assignment, 'links.core/article[0].uuid')
  const [flashId] = useYValue<string>(assignment, 'links.core/flash[0].uuid')
  const [editorialInfoId] = useYValue<string>(assignment, 'links.core/editorial-info[0].uuid')

  const { data: articleStatus, mutate } = useSWR(['articlestatus', articleId, flashId, editorialInfoId], async () => {
    const id = articleId || flashId || editorialInfoId

    if ((id) && session?.accessToken) {
      return await repository?.getMeta({ uuid: id, accessToken: session.accessToken })
    }
  })

  const workflowState = articleStatus?.meta?.workflowState
  const usableAt = workflowState === 'usable'
    ? articleStatus?.meta?.heads['usable']?.created
    : undefined
  const deliverableId = articleId || flashId
  const [assignmentType] = useYValue<string>(assignment, 'meta.core/assignment-type[0].value')
  const [assignmentId] = useYValue<string>(assignment, 'id')
  const [title] = useYValue<string>(assignment, 'title')
  const [description] = useYValue<string>(assignment, 'meta.core/description[0].data.text')
  const [publishTime] = useYValue<string>(assignment, 'data.publish')
  // data.publish is never cleared, so it is only current while withheld; it is
  // kept while the status loads so a scheduled row does not lose its time.
  const shownPublishTime = workflowState === 'usable'
    ? usableAt
    : !workflowState || workflowState === 'withheld' ? publishTime : undefined
  const [startTime] = useYValue<string>(assignment, 'data.start')
  const [endTime] = useYValue<string>(assignment, 'data.end')
  const [publishSlot] = useYValue<string>(assignment, 'data.publish_slot')
  const [authors = []] = useYValue<Block[]>(assignment, 'links.core/author')
  const [wires] = useYValue<Block[]>(assignment, 'links.tt/wire')
  const [slugline] = useYValue<string>(assignment, 'meta.tt/slugline[0].value')
  const [visualStatus] = useYValue<string>(assignment, 'data.status')

  const [showVerifyDialog, setShowVerifyDialog] = useState<boolean>(false)
  const [showCreateDialogPayload, setShowCreateDialogPayload] = useState<boolean>(false)
  const [isLinking, setIsLinking] = useState<boolean>(false)
  const [planningId] = getValueByYPath<string | undefined>(ydoc.ele, 'root.uuid')

  const documentId = articleId || flashId || editorialInfoId
  const isDocument = assignmentType === 'flash' || assignmentType === 'text' || assignmentType === 'editorial-info' || assignmentType === 'timeless'
  const documentLabel = assignmentType
    ? t(`shared:assignmentTypes.${assignmentType}` as TranslationKey)
    : t('common:misc.unknown')

  const openDocument = assignmentType === 'flash' ? openFlash : openArticle
  const { showModal, hideModal } = useModal()

  const assignmentTime = useMemo(() => {
    if (typeof assignmentType !== 'string') {
      return undefined
    }
    if (assignmentType === 'timeless') {
      return undefined
    }
    const endAndStartAreNotEqual = endTime && startTime && endTime !== startTime

    if (isVisualAssignmentType(assignmentType) && startTime) {
      if (endAndStartAreNotEqual) {
        return {
          time: [new Date(startTime), new Date(endTime)],
          tooltip: t('planning:assignment.startEndTime'),
          type: assignmentType
        }
      }

      return {
        time: [new Date(startTime)],
        tooltip: t('planning:assignment.startTime'),
        type: assignmentType
      }
    }

    if (publishSlot) {
      const slotName = getTimeSlotTypes().find((slot) => slot.slots?.includes(publishSlot))?.label
      return {
        time: [slotName],
        tooltip: t('planning:assignment.publishWindow'),
        type: assignmentType
      }
    }

    if (shownPublishTime) {
      return {
        time: [new Date(shownPublishTime)],
        tooltip: t('planning:assignment.publishTime'),
        type: assignmentType
      }
    }

    if (endAndStartAreNotEqual) {
      return {
        time: [new Date(startTime), new Date(endTime)],
        tooltip: t('planning:assignment.startEndTime'),
        type: assignmentType
      }
    }

    if (startTime) {
      return {
        time: [new Date(startTime)],
        tooltip: t('planning:assignment.startTime'),
        type: assignmentType
      }
    }
  }, [shownPublishTime, assignmentType, startTime, endTime, publishSlot, t])

  const isVisualType = isVisualAssignmentType(assignmentType)

  // Small workflow-status badge overlaid on the type icon for non-visual
  // assignments. Visual assignments surface their status as a dropdown in the
  // body instead, since it is interactive.
  const workflowStatusBadge = useMemo(() => {
    if (isVisualType || !workflowState) return undefined
    return getDocumentStatuses().find((s) => s.value === workflowState)
  }, [isVisualType, workflowState])

  const TimeIcon = useMemo(() => {
    const timeIcons: Record<string, React.FC<LucideProps>> = {
      start: Clock1Icon,
      publish: AlarmClockCheckIcon,
      'start-end': ClockFadingIcon
    }

    const type = shownPublishTime
      ? 'publish'
      : endTime && startTime && endTime !== startTime ? 'start-end' : 'start'

    return timeIcons[type] || <></>
  }, [shownPublishTime, endTime, startTime])

  // Open a deliverable (e.g. article, flash, editorial-info) callback helper.
  // For readOnly pass version object
  const onOpenEvent = useCallback(<T extends HTMLElement>(event: MouseEvent<T> | KeyboardEvent, readOnly?: { version: bigint }) => {
    event.preventDefault()
    event.stopPropagation()

    if (documentId) {
      openDocument(
        event,
        {
          id: documentId,
          autoFocus: false,
          version: readOnly?.version.toString()
        },
        undefined,
        undefined,
        event instanceof KeyboardEvent && event.key === ' ')
    } else if (!asDialog && ydoc.ele) {
      // No deliverable yet, show create dialog
      setShowCreateDialogPayload(true)
    }
  }, [documentId, ydoc.ele, openDocument, asDialog])

  // Open a deliverable in read-only preview mode without broadcasting presence
  // to collaborators (preview: true -> visibility: false in useYDocument).
  // Mirrors the Eye button in ApprovalsCard.
  const onPreviewEvent = useCallback(<T extends HTMLElement>(event: MouseEvent<T>) => {
    event.preventDefault()
    event.stopPropagation()

    if (documentId) {
      openDocument(
        event,
        { id: documentId, autoFocus: false, preview: true },
        undefined,
        undefined,
        true)
    }
  }, [documentId, openDocument])

  const rowRef = useRef<HTMLDivElement>(null)
  useEffect(() => {
    if (rowRef?.current && isFocused) {
      rowRef.current.focus()
    }
  })

  useNavigationKeys({
    elementRef: rowRef,
    keys: ['Enter', ' '],
    onNavigation: (event) => {
      if (isDocument) {
        if (event.key === 'Enter' || event.key === ' ') {
          onOpenEvent(event)
        }
      }
    }
  })

  const isUsable = articleStatus?.meta?.workflowCheckpoint === 'usable'

  const menuItems: DotDropdownMenuActionItem[] = [
    {
      label: t('planning:preview'),
      disabled: !documentId,
      icon: EyeIcon,
      item: <T extends HTMLElement>(event: MouseEvent<T>) => {
        const usable = articleStatus?.meta?.workflowState === 'usable'
        const version = articleStatus?.meta?.heads['usable']?.version

        // Usable -> open the frozen usable version (read-only snapshot).
        // Otherwise open the live doc read-only without broadcasting presence.
        if (usable && version) {
          onOpenEvent(event, { version })
        } else {
          onPreviewEvent(event)
        }
      }
    },
    {
      label: t('common:actions.edit'),
      icon: EditIcon,
      disabled: !onSelect,
      item: <T extends HTMLElement>(event: MouseEvent<T>) => {
        event.stopPropagation()
        event.preventDefault()
        if (onSelect) {
          onSelect()
        }
      }
    },
    {
      label: t('common:actions.remove'),
      disabled: isUsable,
      icon: DeleteIcon,
      item: () => {
        setShowVerifyDialog(true)
      }
    },
    {
      label: t('common:actions.move'),
      icon: MoveRightIcon,
      item: () => {
        showModal(
          <Move
            ydoc={ydoc}
            asDialog
            onDialogClose={hideModal}
            original={{
              document: ydoc.provider?.document,
              assignmentId,
              assignmentTitle: title,
              assignment,
              planningId
            }}
          />
        )
      }
    },
    ...(featureFlags.hasPrint
      ? [{
          label: t('planning:assignment.createPrintArticle'),
          disabled: !isDocument,
          icon: LibraryIcon,
          item: () => {
            showModal(
              <CreatePrintArticle
                id={documentId}
                asDialog
                onDialogClose={hideModal}
              />
            )
          }
        }]
      : []),
    ...(assignmentType === 'timeless' && documentId
      ? [{
          label: t('planning:assignment.convertToArticle'),
          disabled: isConverting || articleStatus?.meta?.workflowState === 'used',
          icon: RefreshCwIcon,
          item: () => {
            showModal(
              <ConvertToArticleDialog
                timelessId={documentId}
                onClose={(result) => {
                  hideModal()
                  if (result?.planningId) {
                    openPlanning(undefined, { id: result.planningId })
                  }
                }}
              />
            )
          }
        }]
      : []),
    ...(assignmentType === 'text' && documentId
      ? [{
          label: t('planning:assignment.convertToTimeless'),
          disabled: isConverting || articleStatus?.meta?.workflowState === 'used' || isUsable,
          icon: RefreshCwIcon,
          item: () => {
            showModal(
              <ConvertToTimelessDialog
                articleId={documentId}
                onClose={(result) => {
                  hideModal()
                  if (result?.timelessId) {
                    openArticle(undefined, { id: result.timelessId })
                  }
                }}
              />
            )
          }
        }]
      : [])
  ]
  const selected = articleId && openDocuments.includes(articleId)
  const isUsedTimeless = assignmentType === 'timeless' && workflowState === 'used'

  useRepositoryEvents([
    'core/article',
    'core/article#timeless',
    'core/flash',
    'core/editorial-info'
  ], (event) => {
    if (event.uuid === documentId && event.event === 'status') {
      void mutate()
    }
  })

  return (
    <div
      ref={rowRef}
      tabIndex={0}
      className={cn(
        'group/assrow @container/card relative rounded-md border border-s-[6px] bg-card text-card-foreground',
        'flex flex-col gap-2 text-sm py-3 pl-2 pr-3 transition-colors',
        'hover:bg-muted dark:hover:bg-table-focused',
        'focus:outline-none focus-visible:ring-2 focus-visible:ring-table-selected',
        'focus-visible:ring-offset-2 focus-visible:ring-offset-background',
        getAssignmentStatusBorderClass({
          isVisual: isVisualType,
          visualStatus,
          workflowState
        }),
        selected && 'bg-table-selected focus-visible:outline-table-selected'
      )}
      onClick={(event) => {
        if (showCreateDialogPayload || isLinking) {
          return
        }

        if (isDocument) {
          const isUsable = articleStatus?.meta?.workflowState === 'usable'
            || (assignmentType === 'flash' && articleStatus?.meta?.heads?.['usable']?.version === articleStatus?.meta?.currentVersion)
          const version = articleStatus?.meta?.heads?.['usable']?.version
          onOpenEvent(event, isUsable && version ? { version } : undefined)
        } else if (onSelect) {
          onSelect()
        }
      }}
    >
      <div className={cn(
        'absolute -top-2 -left-3 empty:hidden rounded-full leading-none bg-background border p-0.5 pointer-events-none',
        getAssignmentStatusBadgeBorderClass({
          isVisual: isVisualType,
          visualStatus,
          workflowState
        })
      )}
      >
        <HastIndicator documentId={deliverableId} size={14} />
      </div>

      <div className='flex flex-row gap-3 items-start'>
        <div className='relative shrink-0 pointer-events-none'>
          <AssignmentType
            assignment={assignment}
            editable={!documentId}
            readOnly
          />
          {workflowStatusBadge?.icon && (
            <div className='absolute top-0 right-0 rounded-full leading-none bg-background border border-foreground/30'>
              <workflowStatusBadge.icon
                {...workflowStatusBadge.iconProps}
                size={14}
              />
            </div>
          )}
        </div>

        <div className='flex-1 min-w-0'>
          <div className='flex flex-col gap-3 @md/card:flex-row @md/card:items-start @md/card:justify-between'>
            <div className='min-w-0 flex-1'>
              <div>
                <span
                  className={cn(
                    'font-semibold leading-tight group-hover/assrow:underline',
                    isUsedTimeless && 'text-muted-foreground'
                  )}
                >
                  {title}
                </span>
              </div>

              {(slugline || authors.length > 0) && (
                <Tooltip
                  content={(
                    <div className='flex flex-col gap-0.5'>
                      {slugline && <div>{slugline}</div>}
                      {authors.map((a) => (
                        <div key={a.uuid || a.title}>{a.title}</div>
                      ))}
                    </div>
                  )}
                >
                  <div
                    className={cn(
                      'mt-1 text-sm text-muted-foreground truncate',
                      isUsedTimeless && 'opacity-60'
                    )}
                  >
                    {[slugline, ...authors.map((a) => a.title)].filter(Boolean).join(' · ')}
                  </div>
                </Tooltip>
              )}

              {!!description && (
                <div className='mt-2 font-light text-sm line-clamp-2'>
                  {description}
                </div>
              )}

              <RelatedWires wires={wires} />
            </div>

            {(assignmentTime || isVisualType) && (
              <div className='shrink-0 flex flex-col gap-2 @md/card:w-32'>
                {assignmentTime && (
                  <div className='whitespace-nowrap'>
                    <AssignmentTimeDisplay date={assignmentTime} icon={TimeIcon} />
                  </div>
                )}
                {isVisualType && (
                  <AssignmentStatus
                    isVisualAssignment
                    ydoc={ydoc}
                    path={`meta.core/assignment[${index}].data.status`}
                    workflowState={workflowState}
                  />
                )}
              </div>
            )}
          </div>
        </div>

        {/* Actions: edit pencil + dot menu */}
        <div className='flex items-center gap-1 shrink-0'>
          <Button
            disabled={!onSelect}
            variant='ghost'
            size='sm'
            className='w-9 px-0 hover:bg-accent2 hover:bg-gray-200 dark:hover:bg-table-focused'
            onClick={(event) => {
              event.preventDefault()
              event.stopPropagation()
              if (onSelect) {
                onSelect()
              }
            }}
          >
            <PenIcon size={18} strokeWidth={1.75} className='text-muted-foreground' />
          </Button>

          {!inProgress && <DotMenu items={menuItems} />}
        </div>
      </div>

      {showVerifyDialog && (
        <Prompt
          title={`${t('common:actions.remove')}?`}
          description={`${t('planning:assignment.removeAssignment')}${title ? ' ' + title : ''}?`}
          secondaryLabel={t('common:actions.abort')}
          primaryLabel={t('common:actions.remove')}
          onPrimary={(event) => {
            event.stopPropagation()
            setShowVerifyDialog(false)
            deleteByYPath(ydoc.ele, `meta.core/assignment[${index}]`)
          }}
          onSecondary={() => {
            setShowVerifyDialog(false)
          }}
        />
      )}

      {showCreateDialogPayload && !slugline && assignmentType !== 'flash' && !featureFlags.hasLooseSlugline && (
        <Prompt
          title={t('planning:prompts.slugMissing')}
          description={t('planning:prompts.slugMissingDescription')}
          primaryLabel='Ok'
          onPrimary={(event) => {
            event.preventDefault()
            event.stopPropagation()
            setShowCreateDialogPayload(false)
          }}
        />
      )}

      {showCreateDialogPayload && ydoc.provider?.document && (slugline || assignmentType === 'flash' || featureFlags.hasLooseSlugline) && (
        <CreateDeliverablePrompt
          ydoc={ydoc}
          payload={createPayload(ydoc.provider.document, index, assignmentType) || {}}
          deliverableType={getDeliverableType(assignmentType)}
          title={title || ''}
          documentLabel={documentLabel || ''}
          onClose={(id) => {
            // Prevent multiple simultaneous linking attempts
            if (isLinking) {
              return
            }

            // Capture document reference early to prevent race conditions
            const document = ydoc.provider?.document

            if (!id) {
              setShowCreateDialogPayload(false)
              return
            }

            if (!document) {
              console.error('AssignmentRow: Document reference lost after deliverable creation', { id })
              toast.error(t('errors:toasts.createDeliverableLinkError'))
              setShowCreateDialogPayload(false)
              return
            }

            setIsLinking(true)

            // Link deliverable to assignment with error handling
            try {
              appendDocumentToAssignment({
                document,
                id,
                index,
                slug: '',
                type: getDeliverableType(assignmentType)
              })
            } catch (ex: unknown) {
              const errorMessage = ex instanceof Error ? ex.message : t('errors:messages.unknown')
              const linkError = t('errors:toasts.createDeliverableLinkError')

              console.error('AssignmentRow: Failed to link deliverable to assignment', {
                id,
                assignmentIndex: index,
                error: errorMessage
              })
              toast.error(`${linkError}: ${errorMessage}`)
              setShowCreateDialogPayload(false)
              setIsLinking(false)
              return
            }

            // Snapshot document with error handling
            if (planningId) {
              snapshotDocument(planningId, undefined, document).then(() => {
                const openDocument = assignmentType === 'flash' ? openFlash : openArticle
                openDocument(undefined, { id, planningId }, 'blank')
                setIsLinking(false)
              }).catch((ex: unknown) => {
                const errorMessage = ex instanceof Error ? ex.message : t('errors:toasts.saveError')
                console.error('AssignmentRow: Failed to snapshot planning document', {
                  planningId,
                  deliverableId: id,
                  error: errorMessage
                })
                toast.error(errorMessage)
                setIsLinking(false)
              })
            } else {
              setIsLinking(false)
            }

            setShowCreateDialogPayload(false)
          }}
        />
      )}
    </div>
  )
}
